import { Pencil, Send, UserPlus, Users as UsersIcon } from "lucide-react";
import { Link } from "react-router";
import { toast } from "sonner";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import { useCambiarAcceso, useReenviarInvitacion, useUsuarios } from "@/hooks/useUsuarios";
import { getApiErrorMessage } from "@/lib/api";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { formatearMomento } from "@/lib/fechas";
import { formatearRut } from "@/lib/rut";
import type { Usuario } from "@/lib/usuarios";

const FILAS_SKELETON = 5;

/**
 * Mantenedor de usuarios (Módulo B).
 *
 * Los usuarios NO se eliminan nunca: se desactiva su acceso. El API ni
 * siquiera expone `DELETE /api/users/{user}`, así que la fila ofrece el
 * conmutador y no una papelera.
 */
export function Usuarios() {
  const usuarios = useUsuarios();
  const reenviarInvitacion = useReenviarInvitacion();
  const cambiarAcceso = useCambiarAcceso();
  const { user: autenticado } = useAuth();

  // Los cuatro estados de la consulta (ver `lib/estadoDeConsulta.ts`).
  const estado = estadoDeConsulta(usuarios);

  function handleReenviar(usuario: Usuario) {
    reenviarInvitacion.mutate(usuario.id, {
      onSuccess: () => toast.success(`Invitación reenviada a ${usuario.email}`),
      onError: (error) =>
        toast.error(getApiErrorMessage(error, "No pudimos reenviar la invitación.")),
    });
  }

  function handleAcceso(usuario: Usuario, acceso: boolean) {
    cambiarAcceso.mutate(
      { id: usuario.id, acceso },
      {
        onSuccess: () =>
          toast.success(
            acceso
              ? `${usuario.name} vuelve a tener acceso`
              : `Se desactivó el acceso de ${usuario.name}`,
          ),
        onError: (error) =>
          toast.error(getApiErrorMessage(error, "No pudimos cambiar el acceso.")),
      },
    );
  }

  return (
    <div className="space-y-6 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-muted-foreground text-caption">
            {usuarios.data
              ? `${usuarios.data.length} usuarios registrados`
              : "Personal con acceso al sistema, dado de alta por invitación."}
          </p>
          {/* Microcopy con la regla, donde se toma la decisión: el acceso NO es
              un borrado, y desactivarlo no revoca los tokens ya emitidos —los
              deja sin efecto en el próximo request. */}
          <p className="text-ink-hint mt-0.5 max-w-[70ch] text-xs">
            Los usuarios no se eliminan: se les desactiva el acceso. Al desactivarlo se
            cierra su sesión web y su próximo pedido se rechaza; los tokens de la app móvil
            no se revocan, quedan sin efecto.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/usuarios/nuevo">
            <UserPlus className="h-4 w-4" />
            Crear usuario
          </Link>
        </Button>
      </div>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar los usuarios"
          error={usuarios.error}
          onReintentar={() => usuarios.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que="los usuarios" />
      ) : !estado.cargando && usuarios.data?.length === 0 ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <UsersIcon />
            </EmptyMedia>
            <EmptyTitle>Todavía no hay usuarios</EmptyTitle>
            <EmptyDescription>
              Invita a la primera persona para que pueda acceder al sistema.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button asChild>
              <Link to="/admin/usuarios/nuevo">
                <UserPlus className="h-4 w-4" />
                Crear usuario
              </Link>
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <div className="bg-card overflow-x-auto rounded-xl border">
          <Table className="min-w-[62.5rem]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="px-6">Usuario</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>RUT</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Alta</TableHead>
                <TableHead>Acceso</TableHead>
                <TableHead className="px-6 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {estado.cargando
                ? Array.from({ length: FILAS_SKELETON }).map((_, i) => (
                    // El `aria-label` es lo que deja que un test afirme que la
                    // carga se dibuja (y que borrar esta rama ponga la suite
                    // en rojo).
                    <TableRow key={i} aria-label="Cargando usuario" className="hover:bg-transparent">
                      <TableCell className="px-6">
                        <div className="flex items-center gap-3">
                          <Skeleton className="size-9 rounded-full" />
                          <div className="space-y-1.5">
                            <Skeleton className="h-3.5 w-32" />
                            <Skeleton className="h-3 w-40" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-5 w-24 rounded-full" /></TableCell>
                      <TableCell><Skeleton className="h-3.5 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-3.5 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-32 rounded-full" /></TableCell>
                      <TableCell><Skeleton className="h-3.5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-11 rounded-full" /></TableCell>
                      <TableCell className="px-6 text-right">
                        <Skeleton className="ml-auto h-8 w-20 rounded-md" />
                      </TableCell>
                    </TableRow>
                  ))
                : usuarios.data?.map((usuario) => {
                    const pendiente = usuario.status === "invitacion_pendiente";
                    const reenviando =
                      reenviarInvitacion.isPending && reenviarInvitacion.variables === usuario.id;
                    const cambiandoAcceso =
                      cambiarAcceso.isPending && cambiarAcceso.variables?.id === usuario.id;
                    /* D12 del backend: nadie desactiva su propio acceso, ni
                       siquiera un Super Admin. Se apaga el control en vez de
                       dejar mandar un PATCH que ya se sabe rechazado. */
                    const esUnoMismo = autenticado?.id === usuario.id;

                    return (
                      <TableRow key={usuario.id}>
                        <TableCell className="px-6">
                          <div className="flex items-center gap-3">
                            <InitialsAvatar nombre={usuario.name} className="size-9" />
                            <div>
                              <p className="text-[0.8125rem] font-bold">{usuario.name}</p>
                              <p className="text-muted-foreground text-xs">{usuario.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {usuario.perfil ? (
                            <Badge variant="outline">{usuario.perfil.nombre}</Badge>
                          ) : (
                            <span className="text-muted-foreground font-semibold">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono font-semibold">
                          {/* Mismo guion que el perfil ausente: las filas
                              anteriores a la migración del RUT llegan con null. */}
                          {usuario.rut ? formatearRut(usuario.rut) : "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-semibold">
                          {usuario.cargo ?? "—"}
                        </TableCell>
                        <TableCell>
                          {pendiente ? (
                            <Badge variant="warning">Invitación pendiente</Badge>
                          ) : (
                            <Badge variant="success">Activo</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-medium">
                          {formatearMomento(usuario.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={usuario.acceso}
                              disabled={esUnoMismo || cambiandoAcceso}
                              onCheckedChange={(valor) => handleAcceso(usuario, valor)}
                              aria-label={`Acceso de ${usuario.name}`}
                            />
                            {esUnoMismo && (
                              <span className="text-ink-hint text-xs">Eres tú</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-6">
                          <div className="flex items-center justify-end gap-1.5">
                            {pendiente && (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={reenviando}
                                onClick={() => handleReenviar(usuario)}
                                className="gap-1.5"
                              >
                                <Send className="h-3.5 w-3.5" />
                                {reenviando ? "Reenviando…" : "Reenviar invitación"}
                              </Button>
                            )}
                            <Button asChild variant="ghost" size="sm">
                              <Link to={`/admin/usuarios/${usuario.id}/editar`}>
                                <Pencil className="h-3.5 w-3.5" />
                                Editar
                              </Link>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
